/** csi/comm/JsonResponse */
define([
	"dojo/_base/array",
	"dojo/_base/declare",
	"underscore",
	"src/c8-platform/core/request",
	"csi/locale",
	"./Response"
], function(array, declare, _, {C8RequestError}, locale, Response) {

	const CONNECTION_ERRORS = [
		"INVALID_SESSION"
	];

	const SUCCESS_CODES = {
		"In Progress": true,
		"Successful": true,
		"Some Failed": true
	};

	return declare(Response, {
		// C8RequestError: error if response is not valid
		error: null,

		// Boolean: indicates if one of error codes signaled a bad connection
		_bConnectionError: false,

		// String[]: array of all result nodes for query, regardless of paging
		_refs: false,

		// Number: number of results
		_totalCount: false,

		/**
		 * @override
		 */
		constructor: function(data, xhrResult) {
			if (_.isEmpty(data)) {
				// if nothing in response, then assume a connection error
				this._bConnectionError = true;
			} else if ((data.Status !== undefined) && !SUCCESS_CODES[data.Status]) {
				// check for specific error that signals no authentication - if found, then invalidate response
				// NOTE: Don't use ErrorAdmin as errorMessage in case error surfaces somewhere else besides action controller
				const {Error, Param, Exception} = data;
				const errorMessage = locale.getErrorMessageForCode(Error, Param);
				this._bConnectionError = !Error || CONNECTION_ERRORS.some((e) => Error.indexOf(e) >= 0);
				this.error = new C8RequestError(errorMessage, Error, Param, Exception);
			}
		},

		isConnectionError() {
			return this._bConnectionError;
		},

		isSuccessful() {
			// summary: indicates if server response was successful
			return !this.error;
		},

		/**
		 * @override
		 * Return server error that contains code, params, and exception information
		 */
		getError() {
			return this.error;
		},

		/**
		 * Return an array of CNL's tied to error messages
		 * @return {String[]}
		 */
		getErrorCNLs: function() {
			// TODO: Operation.URL is not always provided (as far as I know), so not sure this should be in JsonResponse as a general function;
			// instead, use getErrorReports and do the rest for each specific use case
			var errorCNLArray = [];
			var errorList = this.data.ERROR_LIST;
			if (errorList) {
				if (Array.isArray(errorList.ErrorReport)) {
					errorList.ErrorReport.forEach(function(e) {
						if (e.Status == "Failed") {
							errorCNLArray.push(e.Operation.URL);
						}
					});
				} else if (errorList.ErrorReport.Status == "Failed") {
					errorCNLArray.push(errorList.ErrorReport.Operation.URL);
				}
			}
			return errorCNLArray;
		},

		/**
		 * Return list of error reports that occur in fault tolerance operations and some (or all) fail
		 * @return {Object[]}
		 */
		getErrorReports: function() {
			const errorList = this.data.ERROR_LIST;
			const errorReports = errorList ? [].concat(errorList.ErrorReport) : [];
			return _.where(errorReports, {Status: "Failed"});
		},

		getFileUrl: function() {
			var obj = this.data.FileInfo;
			return obj ? obj.URL : null;
		},

		getFileUrls: function() {
			var obj = this.data.FileInfo;
			if (!obj) return [];

			if (obj instanceof Array) {
				var n = obj.length;
				var urls = new Array(n);

				for (var i = 0; i < n; ++i) {
					urls[i] = obj[i].URL;
				}

				return urls;
			}

			return [obj.URL];
		},

		getGUID: function() {
			return this.data.GUID;
		},

		/**
		 * @override
		 */
		getTime: function() {
			var s = this.data.Time;
			if (s) {
				const dateStartIndex = s.indexOf('(');
				if (dateStartIndex >= 0) {
					s = s.substring(0, dateStartIndex);
				}
				return Number(s);
			} else {
				return 0;
			}
		},

		hasMoreResults: function() {
			return this.data.HasMoreResults == "true";
		},

		hasReachedCnlLimit: function() {
			return this.data.CnlLimitReached == "true";
		},

		hasReachedCountLimit: function() {
			return this.data.FilterLimitReached == "true";
		},

		hasTotalCount: function() {
			//[YG] This function is called for every page to understand if server returns TotalCount.
			// We need to avoid calculation of Total Count based on the result set, because it could be partial.
			if (this._totalCount === false) {
				var s = this.data.TotalCount;
				if (s == null) return false;
				this._totalCount = Number(s);
			}
			return (this._totalCount != null);
		},

		getTotalCount: function() {
			if (this._totalCount === false) {
				var s = this.data.TotalCount;
				if (s == null) {
					if (this.getCompleteRefs()) {
						this._totalCount = this._refs.length;
					} else {
						this._totalCount = null;
					}
				} else {
					this._totalCount = Number(s);
				}
			}

			return this._totalCount;
		},

		getResultUrls: function() {
			return array.map(this.getResultNodes(), function(node) {return node.$URL;});
		},

		getResultNodes: function() {
			var nodesObj = this.data.NODES;
			return (nodesObj && nodesObj.ResultNode) || [];
		},

		getNonResultNodes: function() {
			var nodesObj = this.data.NODES;
			return nodesObj ? nodesObj.Node : null;
		},

		getNonResultUrls() {
			return array.map(this.getNonResultNodes(), function(node) {return node.$URL;});
		},

		getResultNode: function() {
			var a = this.getResultNodes();
			return (a && (a.length == 1)) ? a[0] : null;
		},

		getNodes: function() {
			// summary: join ResultNodes and Nodes subobjects into one array
			var resultNodes = this.getResultNodes();
			var nodes = this.getNonResultNodes();

			if (resultNodes) {
				return nodes ? resultNodes.concat(nodes) : resultNodes;
			}

			return nodes;
		},

		getNode: function(url) {
			var nodes = this.getNodes();
			if (nodes) {
				var n = nodes.length;
				for (var i = 0; i < n; ++i) {
					if (nodes[i].$URL == url) {
						return nodes[i];
					}
				}
			}

			return null;
		},

		getNodesOfType: function(type) {
			var nodes = this.getNodes();
			if (nodes) {
				var arr = [];
				var n = nodes.length;
				for (var i = 0; i < n; ++i) {
					if (nodes[i].$Type == type) {
						arr.push(nodes[i]);
					}
				}
				if (arr.length)
					return arr;
			}
			return null;
		},

		getNodeOfType: function(type) {
			var nodes = this.getNodes();
			if (nodes) {
				var n = nodes.length;
				for (var i = 0; i < n; ++i) {
					if (nodes[i].$Type == type) {
						return nodes[i];
					}
				}
			}
			return null;
		},

		/** return this kind of array [{Name:1, $: C123},{Name:2, $: C456}] */
		getNameAndUrls: function() {
			return this.getUrls();
		},

		/**  return this kind of array [C123,C456] */
		getUrls: function() {
			var obj = this.data.URLS;
			if (obj != null) {
				obj = obj.URL;
				if (obj && !(obj instanceof Array)) {
					obj = [obj];
				}
			}

			return obj;
		},

		getDeletedUrls: function() {
			var obj = this.data.URLS;
			if (obj != null) {
				obj = obj.Deleted;
				if (obj && !(obj instanceof Array)) {
					obj = [obj];
				}
			}
			return obj;
		},

		getModifiedUrls: function() {
			var obj = this.data.URLS;
			if (obj != null) {
				obj = obj.Modified;
				if (obj && !(obj instanceof Array)) {
					obj = [obj];
				}
			}
			return obj;
		},

		getCompleteRefs: function() {
			if (this._refs === false) {
				var obj = this.data.CompleteResultRefs;
				if (obj) {
					this._refs = (obj.ref instanceof Array) ? obj.ref : [obj.ref];
				} else {
					this._refs = this.getResultUrls();
				}
			}

			return this._refs;
		},

		getValues: function() {
			var data = this.data;
			if (data.VALUE_CNLS) {
				// map format of groups with CNLs to format of values with counts
				// reference: https://centric8.atlassian.net/wiki/spaces/DEV/pages/14844253/ValuesByXML
				var groups = [].concat(data.VALUE_CNLS.Group);

				return _.map(groups, function(obj) {
					var refs = [].concat(obj.ref);
					return {
						Type: obj.Type,
						Value: obj.Value,
						Count: refs.length,
						Refs: refs
					};
				});
			}

			if (data.VALUES) {
				return data.VALUES.Value;
			}

			return null;
		},

		getValuePairs: function() {
			var data = this.data;
			if (data.VALUE_CNLS) {
				// map format of groups with CNLs to format of values with counts
				// reference: https://centric8.atlassian.net/wiki/spaces/DEV/pages/14844256/ValuePairsByXML
				var groups = [].concat(data.VALUE_CNLS.Group);

				return _.map(groups, function(obj) {
					var refs = [].concat(obj.ref);

					return {
						Type: obj.Type,
						Value: obj.Value,
						Type2: obj.Type2,
						Value2: obj.Value2,
						Count: refs.length,
						Refs: refs
					};
				});
			}

			if (data.VALUE_PAIRS) {
				return data.VALUE_PAIRS.Pair;
			}

			return null;
		},

		getReturnValue: function() {
			var v = this.getValues();
			return (v && (v.length == 1)) ? v[0].Value : null;
		},

		getCrews: function() {
			const crews = this.data.CREWS;
			if (!crews) {
				return null;
			}

			// do this because Crew could be a single object or an array of objects
			const crew = crews.Crew;
			return Array.isArray(crew) ? crew : [crew];
		},

		getAttributes: function() {
			return this.data.ATTRIBUTES ? this.data.ATTRIBUTES.Attribute : null;
		},

		getAttributeById: function(attrId) {
			return _.find(this.getAttributes(), function(attr) {
				return attr.Id == attrId;
			});
		},

		getAttributeValueById: function(attrId) {
			var attr = this.getAttributeById(attrId);
			return attr ? attr.Value : undefined;
		},

		/**
		 * Some requests (Change Log as example) will not return complete list of urls
		 * @return {boolean}
		 */
		hasCompleteResultRefs() {
			return !this.hasMoreResults() || !!this.data.CompleteResultRefs;
		},

		/**
		 * Add nodes from given response to this response
			 * @param {Response} response
		 */
		mergeResponse: function(response) {
			var newResultNodes = response.getResultNodes();
			var newNodes = response.getNonResultNodes();
			var nodesObj = this.data.NODES;
			var a;

			a = nodesObj.ResultNode = nodesObj.ResultNode || [];
			a.push.apply(a, newResultNodes || []);

			// this could potentially introduce duplicates if both requests have the same supporting nodes, but they all
			// end up added to the same cache (with no duplicates)
			a = nodesObj.Node = nodesObj.Node || [];
			a.push.apply(a, newNodes || []);
		},

		/**
		 * This can happen when request sets FaultTolerant=true.
		 * @return {boolean} - true if Status is "Some Failed", otherwise false
		 */
		someFailed: function() {
			return this.data.Status == "Some Failed";
		}
	});
});